import { call, put, select, takeEvery } from 'typed-redux-saga'
import { errorHandler, notifyMessageCreator, fileApi, gridSlice } from 'Modules'
import { Deserialize } from 'cerialize'
import { ResponseCatalog, ShortDataModel } from 'Models'
import {
	ProductNS,
	productSlice,
	productsApi,
	ProductAvailableFieldModel,
	ProductModel,
	productsSelector,
	prepareDataBeforeSend,
} from 'Pages/orders'
import { ORDER_DIRECTION } from 'Constants/Global'
import { FieldNames } from 'Constants/FieldNames'

function* getAll(action: ProductNS.GetAll) {
	try {
		const { params, clear } = action.payload
		yield* put(gridSlice.actions.setLoading(true))
		if (clear) {
			yield* put(productSlice.actions.clearAll())
		}
		const productRequestParams = Object.assign(params, {
			order_direction: ORDER_DIRECTION.DESC,
			order_by: FieldNames.ID,
		})
		const res = yield* call(productsApi.getAll, productRequestParams)
		const products = Deserialize(res, ResponseCatalog)
		const entries = products.entries.map((product): ProductModel[] =>
			Deserialize(product, ProductModel)
		)
		if (!clear) {
			const oldProducts = yield* select(productsSelector.getAll)
			products.entries = [...oldProducts.entries, ...entries]
		}
		yield* put(productSlice.actions.setParams(params))
		yield* put(productSlice.actions.setAll(products))
	} catch (error) {
		yield* call(errorHandler, error)
		if (error.status === 403) {
			yield* put(productSlice.actions.setAll(new ResponseCatalog()))
		}
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getOne(action: ProductNS.GetOne) {
	try {
		const res = yield* call(productsApi.getOne, action.payload)
		const product = Deserialize(res, ProductModel)
		yield* put(productSlice.actions.setOne(product))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getFields() {
	try {
		const res = yield* call(productsApi.productFields)
		const fields = res.map(
			(field): ProductAvailableFieldModel => Deserialize(field, ProductAvailableFieldModel)
		)
		yield* put(productSlice.actions.setFields(fields))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* createOne(action: ProductNS.CreateOne) {
	try {
		yield* call(productsApi.createOne, prepareDataBeforeSend(action.payload))
		const params = yield* select(productsSelector.getParams)
		yield* put(productSlice.actions.getAll({ params, clear: true }))
		yield* call(notifyMessageCreator, 'common.product_create.success', {
			data: [action.payload.name],
		})
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* deleteOne(action: ProductNS.DeleteOne) {
	try {
		const res = yield* call(productsApi.deleteOne, action.payload.id)
		if (res.result === 'ok') {
			yield* put(productSlice.actions.setOneUpdate({ ...action.payload, is_deleted: true }))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* restoreOne(action: ProductNS.RestoreOne) {
	try {
		const res = yield* call(productsApi.restoreOne, action.payload.id)
		if (res.result === 'ok') {
			yield* put(productSlice.actions.setOneUpdate({ ...action.payload, is_deleted: false }))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* updateOne(action: ProductNS.UpdateOne) {
	try {
		const { id, product, deleteImageUrl } = action.payload
		const res = yield* call(productsApi.updateOne, id, prepareDataBeforeSend(product))
		if (deleteImageUrl) yield* call(fileApi.deleteFile, deleteImageUrl)
		yield* put(productSlice.actions.setOneUpdate(res))
		yield* call(notifyMessageCreator, 'common.update.success', { data: [product.name] })
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getProductGroups() {
	try {
		const res = yield* call(productsApi.productGroups)
		const productGroups = Deserialize(res, ResponseCatalog)
		productGroups.entries = res.entries.map(
			(product): ShortDataModel => Deserialize(product, ShortDataModel)
		)
		yield* put(productSlice.actions.setProductGroups(productGroups))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export const productSaga = function* saga() {
	yield* takeEvery(productSlice.actions.getFields.type, getFields)
	yield* takeEvery(productSlice.actions.createOne.type, createOne)
	yield* takeEvery(productSlice.actions.deleteOne.type, deleteOne)
	yield* takeEvery(productSlice.actions.getAll.type, getAll)
	yield* takeEvery(productSlice.actions.getOne.type, getOne)
	yield* takeEvery(productSlice.actions.updateOne.type, updateOne)
	yield* takeEvery(productSlice.actions.restoreOne.type, restoreOne)
	yield* takeEvery(productSlice.actions.getProductGroups.type, getProductGroups)
}
